'use client';

// ✅ src/App.js (FULL - UPDATED)
// ✅ NEW: Hero now has Earn Money button -> routes to /earn
// ✅ NEW: Added /about route + Hero "What's this site about?" button handler
// ✅ FIX: Keeps PUBLIC Contact Us route: /contactus
// ✅ Keeps: redirect boot, auth guards, seller setup gate, admin gate, paystack callback public

import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import Hero from "./components/Hero";
import LoginPage from "./pages/LoginPage";

import TermsScreen from "./pages/TermsScreen";
import PrivacyScreen from "./pages/PrivacyScreen";
import ChooseScreen from "./components/screens/ChooseScreen";
import CreateStoreScreen from "./components/screens/CreateStoreScreen";
import SellerDashboardScreen from "./components/screens/SellerDashboardScreen";

import AddProductScreen from "./components/screens/AddProductScreen";
import ProductsScreen from "./components/screens/ProductsScreen";
import OrdersScreen from "./components/screens/OrdersScreen";
import OrderDetailsScreen from "./components/screens/OrderDetailsScreen";
import PaymentsDeliveryScreen from "./components/screens/PaymentsDeliveryScreen";
import StoreSettingsScreen from "./components/screens/StoreSettingsScreen";
import NotificationsScreen from "./components/screens/NotificationsScreen";
import ReportsScreen from "./components/screens/ReportsScreen";

import SellerProductViewScreen from "./components/screens/SellerProductViewScreen";
import EditProductScreen from "./components/screens/EditProductScreen";

import PublicStoreScreen from "./components/screens/PublicStoreScreen";
import PublicProductScreen from "./components/screens/PublicProductScreen";
import CheckoutScreen from "./components/screens/CheckoutScreen";

import CartScreen from "./components/screens/CartScreen";
import BrowseStoresScreen from "./components/screens/BrowseStoresScreen";
import EarnMoneyScreen from "./components/screens/EarnMoneyScreen";
import BuyerProfileScreen from "./components/screens/BuyerProfileScreen";

import ContactUsScreen from "./components/screens/ContactUsScreen";

// ✅ NEW SCREEN (only addition)
import AboutSiteScreen from "./components/screens/AboutSiteScreen";

// ✅ NEW: Store reviews route screen
import StoreReviewsScreen from "./components/screens/StoreReviewsScreen";

import AdminScreen from "./components/admin/AdminScreen";

import SellerPayScreen from "./components/screens/SellerPayScreen";
import SellerSubscriptionScreen from "./components/screens/SellerSubscriptionScreen";
import SellerPayCallbackScreen from "./components/screens/SellerPayCallbackScreen";

import { auth, db } from "./lib/firebase";
import { onAuthStateChanged, onIdTokenChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

// ✅ IMPORTANT: initAuth must run BEFORE redirect + auth listener
import { initAuth, handleGoogleRedirect } from "./lib/auth";

/* ----------------- HELPERS ----------------- */
function usesPasswordProvider(user) {
  return (user?.providerData || []).some((p) => p?.providerId === "password");
}

/* ----------------- AUTH GUARDS ----------------- */
function RequireAuth({ user, loading, children }) {
  const location = useLocation();

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;

  if (!user)
    return <Navigate to="/login" replace state={{ next: location.pathname }} />;

  // ✅ Email verification gate (only for email/password users)
  if (usesPasswordProvider(user) && !user.emailVerified) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          next: location.pathname,
          msg: "Please verify your email to continue.",
        }}
      />
    );
  }

  return children;
}

function RequireSellerSetup({ user, loading, children }) {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (loading) return;
      if (!user) {
        if (!alive) return;
        setAllowed(false);
        setChecking(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "stores", user.uid));
        const store = snap.exists() ? snap.data() : null;
        const setupDone = !!store?.setupDone;

        if (!alive) return;
        setAllowed(snap.exists() && setupDone);
      } catch {
        if (!alive) return;
        setAllowed(false);
      } finally {
        if (alive) setChecking(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user, loading]);

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (checking) return <div style={{ padding: 16 }}>Loading…</div>;
  if (!allowed)
    return <Navigate to="/choose" replace state={{ next: location.pathname }} />;

  return children;
}

function RequireAdmin({ user, loading, children }) {
  const [checking, setChecking] = useState(true);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (loading) return;
      if (!user) {
        if (!alive) return;
        setOk(false);
        setChecking(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "stores", user.uid));
        const store = snap.exists() ? snap.data() : null;
        const role = String(store?.role || "").toLowerCase().trim();
        const isAdmin = role === "admin" || store?.isAdmin === true;
        if (!alive) return;
        setOk(isAdmin);
      } catch {
        if (!alive) return;
        setOk(false);
      } finally {
        if (alive) setChecking(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user, loading]);

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (checking) return <div style={{ padding: 16 }}>Loading…</div>;
  if (!ok) return <Navigate to="/seller" replace />;

  return children;
}

/* ----------------- ROUTES ----------------- */
function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [autoFinishDone, setAutoFinishDone] = useState(false);

  // ✅ Persist next path across redirects
  const saveNext = (p) => {
    try {
      sessionStorage.setItem("postAuthNext", String(p || "/"));
    } catch {}
  };
  const readNext = () => {
    const fromState = location.state?.next;
    if (fromState) return fromState;
    try {
      const saved = sessionStorage.getItem("postAuthNext");
      return saved || "/";
    } catch {
      return "/";
    }
  };
  const clearNext = () => {
    try {
      sessionStorage.removeItem("postAuthNext");
    } catch {}
  };

  const goToLogin = (nextPath) => {
    saveNext(nextPath || "/");
    navigate("/login", { state: { next: nextPath || "/" } });
  };

  const handleAuthSuccess = async () => {
    const u = auth.currentUser;
    const next = readNext();

    console.log(
      "[APP] handleAuthSuccess() currentUser:",
      u?.uid || null,
      "next:",
      next
    );

    if (!u) {
      clearNext();
      navigate("/", { replace: true });
      return;
    }

    // ✅ Email verification gate (only password users)
    if (usesPasswordProvider(u) && !u.emailVerified) {
      console.log("[APP] blocked: password user not verified");
      return;
    }

    // ✅ seller routes need store setup
    if (next.startsWith("/seller")) {
      try {
        const storeSnap = await getDoc(doc(db, "stores", u.uid));
        const store = storeSnap.exists() ? storeSnap.data() : null;
        const hasStore = storeSnap.exists();
        const setupDone = !!store?.setupDone;

        clearNext();

        // New user / not setup -> choose
        if (!hasStore || !setupDone) {
          navigate("/choose", { replace: true });
          return;
        }

        navigate("/seller", { replace: true });
        return;
      } catch {
        clearNext();
        navigate("/choose", { replace: true });
        return;
      }
    }

    clearNext();
    navigate(next, { replace: true });
  };

  // ✅ Boot effect
  useEffect(() => {
    let alive = true;
    let unsub = null;
    let unsubToken = null;

    (async () => {
      console.log("[APP] initAuth + redirect boot starting...");

      try {
        await initAuth();

        console.log("[APP] calling handleGoogleRedirect() on app load...");
        const res = await handleGoogleRedirect();

        // ✅ NOTE: with the updated auth.js, handleGoogleRedirect returns { user, isNewUser }
        console.log(
          "[APP] handleGoogleRedirect returned uid:",
          res?.user?.uid || null,
          "isNewUser:",
          !!res?.isNewUser
        );

        // We don't navigate here — we let /login auto-finish logic handle it
      } catch (e) {
        console.error("[APP] boot error:", e);
      }

      unsub = onAuthStateChanged(auth, async (u) => {
        console.log("[APP] onAuthStateChanged:", u ? u.uid : null);

        if (!alive) return;

        setUser(u || null);
        setAuthLoading(false);

        if (!u) return;

        try {
          await getDoc(doc(db, "users", u.uid));
        } catch {}
      });

      // ✅ EXTRA: token listener debug
      unsubToken = onIdTokenChanged(auth, (u) => {
        console.log("[APP] onIdTokenChanged:", u ? u.uid : null);
      });
    })();

    return () => {
      alive = false;
      if (typeof unsub === "function") unsub();
      if (typeof unsubToken === "function") unsubToken();
    };
  }, []);

  // ✅ Auto-finish after auth becomes available on /login (only once per session)
  useEffect(() => {
    console.log("[APP] auto-finish check:", {
      authLoading,
      hasUser: !!user,
      path: location.pathname,
      autoFinishDone,
    });

    if (authLoading) return;
    if (!user) return;
    if (location.pathname !== "/login") return;
    if (autoFinishDone) return;

    setAutoFinishDone(true);
    handleAuthSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, location.pathname, autoFinishDone]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Hero
            // ✅ CHANGED: all CTAs should go to login (seller flow)
            onBuyClick={() => goToLogin("/seller")}
            onSellClick={() => goToLogin("/seller")}
            // ✅ About
            onAboutClick={() => navigate("/about")}
            // ✅ NEW: Earn Money (public)
            onEarnClick={() => navigate("/earn")}
          />
        }
      />

      {/* ✅ NEW: PUBLIC About page */}
      <Route
        path="/about"
        element={<AboutSiteScreen goBack={() => navigate(-1)} />}
      />

      <Route
        path="/login"
        element={
          <LoginPage onSuccess={handleAuthSuccess} goBack={() => navigate(-1)} />
        }
      />

      <Route path="/terms" element={<TermsScreen />} />
      <Route path="/privacy" element={<PrivacyScreen />} />

      {/* PUBLIC */}
      <Route path="/browse" element={<BrowseStoresScreen />} />

      {/* ✅ NEW: PUBLIC Store Reviews */}
      <Route path="/reviews/store/:storeId" element={<StoreReviewsScreen />} />

      {/* ✅ FIX: PUBLIC Contact Us (BrowseStoresScreen uses /contactus) */}
      <Route
        path="/contactus"
        element={<ContactUsScreen goBack={() => navigate(-1)} />}
      />

      {/* ✅ Earn Money */}
      <Route
        path="/earn"
        element={<EarnMoneyScreen goBack={() => navigate(-1)} />}
      />

      <Route path="/s/:storeSlug" element={<PublicStoreScreen />} />
      <Route path="/p/:productId" element={<PublicProductScreen />} />

      {/* CART */}
      <Route
        path="/cart"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <CartScreen goBack={() => navigate(-1)} />
          </RequireAuth>
        }
      />

      {/* CHECKOUT */}
      <Route
        path="/checkout"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <CheckoutScreen />
          </RequireAuth>
        }
      />

      {/* BUYER PROFILE */}
      <Route
        path="/me"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <BuyerProfileScreen goBack={() => navigate(-1)} />
          </RequireAuth>
        }
      />

      {/* CHOOSE / CREATE STORE */}
      <Route
        path="/choose"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <ChooseScreen
              goBrowse={() => navigate("/browse")}
              goCreateStore={() => navigate("/createStore")}
              goBack={() => navigate("/")}
            />
          </RequireAuth>
        }
      />

      <Route
        path="/createStore"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <CreateStoreScreen
              goBack={() => navigate("/choose")}
              onNext={() => navigate("/seller")}
            />
          </RequireAuth>
        }
      />

      {/* SELLER */}
      <Route
        path="/seller"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <SellerDashboardScreen
                goBack={() => navigate("/")}
                goManageProducts={() => navigate("/seller/products")}
                goAddProduct={() => navigate("/seller/add")}
                goOrders={() => navigate("/seller/orders")}
                goPaymentsAndDelivery={() =>
                  navigate("/seller/payments-delivery")
                }
                goStoreProfile={() => navigate("/seller/store-settings")}
                goSettings={() => navigate("/seller/store-settings")}
                goNotifications={() => navigate("/seller/notifications")}
                goReports={() => navigate("/seller/reports")}
                goContactUs={() => navigate("/seller/contact-us")}
                goAdmin={() => navigate("/seller/admin")}
              />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/add"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <AddProductScreen goBack={() => navigate("/seller")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/products"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <ProductsScreen goBack={() => navigate("/seller")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/products/:productId"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <SellerProductViewScreen goBack={() => navigate("/seller/products")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/products/:productId/edit"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <EditProductScreen goBack={() => navigate("/seller/products")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/orders"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <OrdersScreen goBack={() => navigate("/seller")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/orders/:orderId"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <OrderDetailsScreen />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/payments-delivery"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <PaymentsDeliveryScreen goBack={() => navigate("/seller")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/store-settings"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <StoreSettingsScreen goBack={() => navigate("/seller")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/notifications"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <NotificationsScreen goBack={() => navigate("/seller")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/reports"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <ReportsScreen goBack={() => navigate("/seller")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/contact-us"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <ContactUsScreen goBack={() => navigate("/seller")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/subscription"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <SellerSubscriptionScreen goBack={() => navigate("/seller")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route
        path="/seller/pay"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <SellerPayScreen goBack={() => navigate("/seller/subscription")} />
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      {/* ✅ PAYSTACK CALLBACK MUST BE PUBLIC */}
      <Route path="/seller/pay/callback" element={<SellerPayCallbackScreen />} />

      <Route
        path="/seller/admin"
        element={
          <RequireAuth user={user} loading={authLoading}>
            <RequireSellerSetup user={user} loading={authLoading}>
              <RequireAdmin user={user} loading={authLoading}>
                <AdminScreen goBack={() => navigate("/seller/products")} />
              </RequireAdmin>
            </RequireSellerSetup>
          </RequireAuth>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
